import { InjectionToken } from '@angular/core';
import { EMBEDDED_QUERY_PARAM, MAX_OPEN_PROJECTS, MAX_OPEN_PROJECT_NAME_LENGTH, OpenProjectKind, OpenProjectRecord } from '@infinitform/platform-core';

/**
 * Where an open project's editor is: never mounted (`cold`), mounting
 * (`booting`), shown (`live`), paused in the background (`warm`), unloading
 * (`hibernating`), unloaded with a picture (`hibernated`) or did not start
 * (`failed`).
 */
export type OpenProjectState = 'cold' | 'booting' | 'live' | 'warm' | 'hibernating' | 'hibernated' | 'failed';

/**
 * Mounted editors kept paused behind the visible one. Defaults to every open
 * project staying loaded: with the ten-project cap that is one live plus nine
 * paused. A deployment can inject a lower value to go back to unloading the
 * least recently used background editors once there are more than that many.
 */
export const OPEN_PROJECTS_WARM_LIMIT = new InjectionToken<number>('OpenProjectsWarmLimit', {
  providedIn: 'root',
  factory: () => MAX_OPEN_PROJECTS - 1,
});
/** How long a new editor has to say `ready`. */
export const READY_TIMEOUT_MS = 30_000;
/** How long an unloading editor has to acknowledge before its frame is removed anyway. */
export const HIBERNATE_ACK_TIMEOUT_MS = 5_000;
/** Delay before the open list is written to the user settings. */
export const PERSIST_DEBOUNCE_MS = 1_000;
/** The cabinet skips renewing on an editor's `auth-request` if it renewed within this long. */
export const AUTH_REQUEST_RENEW_COOLDOWN_MS = 30_000;

/** The name given to a project opened by direct route visit, before its real name is known. */
export const PLACEHOLDER_PROJECT_NAME = 'Untitled project';

const PROJECT_ID = /^[a-f0-9]{24}$/i;
const KINDS: readonly OpenProjectKind[] = ['editor', 'mold', 'jaws'];

export interface PoolMember {
  id: string;
  state: OpenProjectState;
  dirty: boolean;
  lastActiveAt: string;
}

export function isProjectId(value: string): boolean {
  return PROJECT_ID.test(value);
}

/** A project's editor URL; `embedded` adds the query param the editor uses to render for a frame. */
export function editorProjectUrl(editorAddress: string, id: string, options: { embedded?: boolean } = {}): string {
  const url = `${editorAddress}/${id}`;
  return options.embedded ? `${url}?${EMBEDDED_QUERY_PARAM}=1` : url;
}

function isIsoDate(value: unknown): value is string {
  return typeof value === 'string' && !Number.isNaN(Date.parse(value));
}

function toRecord(item: unknown): OpenProjectRecord | null {
  if (typeof item !== 'object' || item === null) return null;
  const source = item as Record<string, unknown>;
  const id = source['id'];
  const name = source['name'];
  const kind = KINDS.find((known) => known === source['kind']);
  const openedAt = source['openedAt'];
  const lastActiveAt = source['lastActiveAt'];
  if (typeof id !== 'string' || !isProjectId(id)) return null;
  if (typeof name !== 'string' || !name.trim()) return null;
  if (!kind || !isIsoDate(openedAt) || !isIsoDate(lastActiveAt)) return null;
  return { id, name: name.slice(0, MAX_OPEN_PROJECT_NAME_LENGTH), kind, openedAt, lastActiveAt };
}

/** The `limit` most recently used records, in their original order. */
export function capByRecency(records: readonly OpenProjectRecord[], limit: number): OpenProjectRecord[] {
  if (records.length <= limit) return [...records];
  const kept = new Set(
    [...records]
      .sort((a, b) => b.lastActiveAt.localeCompare(a.lastActiveAt))
      .slice(0, limit)
      .map((record) => record.id)
  );
  return records.filter((record) => kept.has(record.id));
}

/** The stored open list with malformed and repeated entries removed, at most ten. */
export function sanitizeOpenProjects(value: unknown): OpenProjectRecord[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const records: OpenProjectRecord[] = [];
  for (const item of value) {
    const record = toRecord(item);
    if (!record || seen.has(record.id)) continue;
    seen.add(record.id);
    records.push(record);
  }
  return capByRecency(records, MAX_OPEN_PROJECTS);
}

/** True for a project opened by a direct route visit, before its real name and kind were known. */
function isPlaceholder(record: OpenProjectRecord): boolean {
  return record.name === PLACEHOLDER_PROJECT_NAME;
}

/**
 * The stored list, kept whole (it is already at most `MAX_OPEN_PROJECTS`,
 * via `sanitizeOpenProjects`), plus projects opened before it arrived. Those
 * local projects win over a stored copy, but when there isn't room for all
 * of them, the least recently used local additions are dropped instead of a
 * saved project — the caller is the one adding projects while the restore is
 * still in flight, so it is the one that should notice and lose the race, not
 * a project the user already had open before this page load. A local
 * placeholder added from a direct route visit takes the stored record's name
 * and kind instead, since the stored copy is the real one.
 */
export function mergeRestored(server: readonly OpenProjectRecord[], local: readonly OpenProjectRecord[]): OpenProjectRecord[] {
  const serverById = new Map(server.map((record) => [record.id, record]));
  const localIds = new Set(local.map((record) => record.id));
  const stored = server.filter((record) => !localIds.has(record.id));
  const added = local.filter((record) => !serverById.has(record.id));
  const keptAddedIds = new Set(capByRecency(added, Math.max(0, MAX_OPEN_PROJECTS - server.length)).map((record) => record.id));
  const keptLocal = local.filter((record) => serverById.has(record.id) || keptAddedIds.has(record.id));
  const resolvedLocal = keptLocal.map((record) => {
    if (!isPlaceholder(record)) return record;
    const match = serverById.get(record.id);
    return match ? { ...record, name: match.name, kind: match.kind } : record;
  });
  return [...stored, ...resolvedLocal];
}

/** Background editors to unload so at most `warmLimit` stay mounted behind the visible one; never one with unsaved changes. */
export function pickHibernations(members: readonly PoolMember[], activeId: string | null, warmLimit: number): string[] {
  const background = members.filter((member) => member.id !== activeId && (member.state === 'warm' || member.state === 'booting'));
  const excess = background.length - warmLimit;
  if (excess <= 0) return [];
  return background
    .filter((member) => !member.dirty)
    .sort((a, b) => a.lastActiveAt.localeCompare(b.lastActiveAt))
    .slice(0, excess)
    .map((member) => member.id);
}
